import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";

import reorder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import { canCancelOrder, canRmaOrder } from "@insite/client-framework/Store/Pages/OrderDetails/OrderDetailsSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ShareEntityButton, { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import { OrderDetailsPageContext } from "@insite/content-library/Pages/OrderDetailsPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { HiddenProps } from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext, { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CancelOrderModal from "@MasterBluePrint/Components/CancelOrderModal";
import DocumentModal from "@MasterBluePrint/Components/DocumentsModal";
import Document from "@MasterBluePrint/Icons/Document";
import PrintInvoice from "@MasterBluePrint/Icons/PrintInvoice";
import ReturnIcon from "@MasterBluePrint/Icons/ReturnIcon";
import { OrderStatusPageContext } from "@MasterBluePrint/Overrides/Pages/OrderStatusPage";
import getOrderDocuments from "@MasterBluePrint/Store/Components/OrderDocumentsModal/Handlers/GetOrderDocuments";
import cancelOrder from "@MasterBluePrint/Store/Pages/OrderDetails/Handlers/CancelOrder";
import { OrderDocumentsModel, OrderDocumentsRequestModel, OrderDocumentsResponseModel } from "@MasterBluePrint/Types/ApiModels";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    buttonsOrder = "buttonsOrder",
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

const enum buttons {
    cancel = "Cancel",
    print = "Print",
    email = "Email",
    reorder = "Reorder",
    rma = "Rma",
    printinvoice = "PrintInvoice",
    documents = "Documents"
}

interface ButtonMapper {
    [key: string]: {
        button: any;
        clickable: any;
        showButtonOnTablet?: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonsOrder]: ButtonModel[];
        [fields.generateEmailAttachmentFromWebpage]?: boolean;
    };
}

interface ButtonModel {
    fields: {
        name: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const order = getOrderState(state, state.pages.orderDetails.orderNumber).value;
    const settingsCollection = getSettingsCollection(state);
    return {
        order,
        canCancel: canCancelOrder(state, order),
        allowRma: canRmaOrder(state, order),
        rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
        canReorderItems: settingsCollection.orderSettings.canReorderItems,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        isReordering: state.pages.orderDetails.isReordering,
    };
};

const mapDispatchToProps = {
    cancelOrder,
    reorder,
    getOrderDocuments
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasToasterContext;

export interface OrderDetailsButtonSetStyles {
    buttonsWrapper?: InjectableCss;
    buttonHidden?: HiddenProps;
    buttonGridContainer?: GridContainerProps;
    tabletButtonHidden?: HiddenProps;
    tabletMenuHidden?: HiddenProps;
    menuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    cancelButton?: ButtonPresentationProps;
    cancelClickable?: ClickablePresentationProps;
    printButton?: ButtonPresentationProps;
    documentButton?:ButtonPresentationProps;
    printClickable?: ClickablePresentationProps;
    reorderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    reorderClickable?: ClickablePresentationProps;
    reorderClickableSpinner?: LoadingSpinnerProps;
    rmaButton?: ButtonPresentationProps;
    rmaClickable?: ClickablePresentationProps;
    buttonWrapper?: InjectableCss;
    printinvoiceButton?: ButtonPresentationProps;
    printinvoiceClickable?: ClickablePresentationProps;
}

export const orderDetailsButtonSetStyles: OrderDetailsButtonSetStyles = {
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            clear: both;

            .separator {
                border-left: 1px solid #e4e8f0;
                height: 14px;
                margin: 0 10px;
            }

            > span.separator:first-child {
                display: none;
            }

            .btn-wrapper {
                a,
                button {
                    &:before {
                        content: "";
                        display: inline-block;
                        border-left: 1px solid #e4e8f0;
                        height: 14px;
                        margin: 0 10px;
                        position: relative;
                        left: -10px;
                        top: 2px;
                    }
                }

                &:first-child {
                    a,
                    button {
                        &:before {
                            content: none;
                        }
                    }
                    &:before {
                        content: none;
                    }
                }
            }

            .hide-empty-element {
                display: none;
            }

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            align-content: center;
                            align-items: center;

                            > div {
                                display: flex;
                                align-items: center;
                                align-content: center;
                            }

                            .btn-wrapper {
                                a,
                                button {
                                    &:before {
                                        content: "";
                                        display: inline-block;
                                        border-left: 1px solid #e4e8f0;
                                        height: 14px;
                                        margin: 0 10px;
                                        position: relative;
                                        left: -1px;
                                        top: 1px;
                                    }
                                }

                                &:first-child {
                                    a,
                                    button {
                                        &:before {
                                            content: none;
                                        }
                                    }
                                    &:before {
                                        content: none;
                                    }
                                }
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};
        `,
    },
    rmaClickable: {
        css: css`
            margin-left: 10px;
            margin-right: 15px;
            color: #002554;
            font-family: Lato;
            font-size: 14px;
            letter-spacing: 0.16px;
            line-height: 18px;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: flex;
                            margin: 0 5px;
                            align-items: center;
                            align-content: center;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};

            span {
                display: inline-block;
                vertical-align: middle;

                &:first-child {
                    width: 17px;
                }

                &:last-child {
                    margin-left: 5px;
                    color: #002554;
                    font-family: Lato;
                    font-size: 14px;
                    letter-spacing: 0.16px;
                    line-height: 18px;
                }

                ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                // max-width:70px;
                            `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};
            }

            svg {
                display: inline-block;
                vertical-align: middle;
                font-size: 14px;
                color: #002554;
                width: 19px;
                height: 19px;
            }

            &:hover,
            &:active,
            &:focus {
                color: #002554 !important;
                border: none !important;
                background: none !important;
                padding: 0;
            }
        `,
    },
    shareEntityButtonStyles: {
        button: {
            buttonType: "outline",
            variant: "secondary",
            css: css`
                border: none;
                background: none;
                padding: 0;
                color: #002554;
                font-family: Lato;
                font-size: 14px;
                letter-spacing: 0.16px;
                line-height: 18px;
                float: right;
                height: auto;
                margin-left: 10px;
                margin-right: 15px;

                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: flex;
                                margin: 0 5px;
                                align-items: center;
                                align-content: center;
                            `,
                            null,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )};

                span {
                    display: inline-block;
                    vertical-align: middle;

                    &:last-child {
                        margin-left: 5px;
                        color: #002554;
                        font-family: Lato;
                        font-size: 14px;
                        letter-spacing: 0.16px;
                        line-height: 18px;
                    }

                    ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                    // max-width:70px;
                                `,
                            null,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )};
                }

                svg {
                    display: inline-block;
                    vertical-align: middle;
                    font-size: 14px;
                    color: #002554;
                    width: 21px;
                    height: 20px;
                }

                &:hover,
                &:active,
                &:focus {
                    color: #002554 !important;
                    border: none !important;
                    background: none !important;
                    padding: 0;
                }
            `,
        },
    },
    buttonHidden: {
        below: "lg",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    tabletButtonHidden: {
        below: "md",
        above: "md",
    },
    tabletMenuHidden: {
        below: "md",
        above: "md",
    },
    menuHidden: {
        above: "sm",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    buttonWrapper: {
        css: css`
            padding-bottom: 0.9375rem;
        `,
    },
    cancelButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            border: none;
            background: none;
            padding: 0;
            color: #002554;
            font-family: Lato;
            font-size: 14px;
            letter-spacing: 0.16px;
            line-height: 18px;
            float: right;
            height: auto;
            margin-left: 10px;
            margin-right: 15px;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: flex;
                            margin: 0 5px;
                            align-items: center;
                            align-content: center;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};

            span {
                display: inline-block;
                vertical-align: middle;

                &:last-child {
                    margin-left: 5px;
                    color: #002554;
                    font-family: Lato;
                    font-size: 14px;
                    letter-spacing: 0.16px;
                    line-height: 18px;
                }
            }

            svg {
                display: inline-block;
                vertical-align: middle;
                font-size: 14px;
                color: #002554;
                width: 20px;
                height: 20px;
            }

            &:hover,
            &:active,
            &:focus {
                color: #002554 !important;
                border: none !important;
                background: none !important;
                padding: 0;
            }
        `,
    },
    documentButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            border: none;
            background: none;
            padding: 0;
            color: #002554;
            font-family: Lato;
            font-size: 14px;
            letter-spacing: 0.16px;
            line-height: 18px;
            float: right;
            height: auto;
            margin-left: 10px;
            margin-right: 15px;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                        display: flex;
                        margin: 0 5px;
                        align-items: center;
                        align-content: center;
                        display: none;
                    }
                    `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};

            span {
                display: inline-block;
                vertical-align: middle;

                &:last-child {
                    margin-left: 5px;
                    color: #002554;
                    font-family: Lato;
                    font-size: 14px;
                    letter-spacing: 0.16px;
                    line-height: 18px;
                }

                ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                // max-width:70px;
                            `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};
            }

            svg {
                display: inline-block;
                vertical-align: middle;
                font-size: 14px;
                color: #002554;
                width: 13px;
                height: 18px;
            }
            &:hover,
            &:active,
            &:focus {
                color: #002554 !important;
                border: none !important;
                background: none !important;
                padding: 0;
            }
        `,
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            border: none;
            background: none;
            padding: 0;
            color: #002554;
            font-family: Lato;
            font-size: 14px;
            letter-spacing: 0.16px;
            line-height: 18px;
            float: right;
            height: auto;
            margin-left: 10px;
            margin-right: 15px;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                        display: flex;
                        margin: 0 5px;
                        align-items: center;
                        align-content: center;
                        display: none;
                    }
                    `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};

            span {
                display: inline-block;
                vertical-align: middle;

                &:last-child {
                    margin-left: 5px;
                    color: #002554;
                    font-family: Lato;
                    font-size: 14px;
                    letter-spacing: 0.16px;
                    line-height: 18px;
                }

                ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                // max-width:70px;
                            `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};
            }

            svg {
                display: inline-block;
                vertical-align: middle;
                font-size: 14px;
                color: #002554;
                width: 21px;
                height: 20px;
            }
            &:hover,
            &:active,
            &:focus {
                color: #002554 !important;
                border: none !important;
                background: none !important;
                padding: 0;
            }
        `,
    },
    reorderButton: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    rmaButton: {
        buttonType: "solid",
        variant: "secondary",
    },
    spinner: {
        size: 22,
        css: css`
            margin: 0.5rem 3.125rem 0 2.125rem;
        `,
    },
    reorderClickableSpinner: {
        size: 22,
        css: css`
            margin: 0.625rem 0 0.31250625rem 0.9375rem;
        `,
    },
    printinvoiceButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            border: none;
            background: none;
            padding: 0;
            font-size: 14px;
            letter-spacing: 0.16px;
            line-height: 18px;
            height: auto;
            margin-left: 10px;
            margin-right: 15px;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: flex;
                            margin: 0 5px;
                            align-items: center;
                            align-content: center;
                            display: none;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};

            span {
                display: inline-block;
                vertical-align: middle;

                &:last-child {
                    margin-left: 5px;
                    color: #002554;
                    font-family: Lato;
                    font-size: 14px;
                    letter-spacing: 0.16px;
                    line-height: 18px;
                }

                ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                // max-width:70px;
                            `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};
            }

            svg {
                display: inline-block;
                vertical-align: middle;
            }
            &:hover,
            &:active,
            &:focus {
                color: #333f48 !important;
                border: none !important;
                background: none !important;
                padding: 0;
            }
        `,
    },
};

/**
 * @deprecated Use orderDetailsButtonSetStyles instead.
 */
export const emailButtonStyles = orderDetailsButtonSetStyles;
const styles = orderDetailsButtonSetStyles;

const OrderDetailsButtonSet: React.FC<Props> = ({
    order,
    cancelOrder,
    reorder,
    canCancel,
    allowRma,
    fields,
    rmaLink,
    canReorderItems,
    showAddToCartConfirmationDialog,
    isReordering,
    history,
    getOrderDocuments
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [isCancelOrderModal, setIsCancelOrderModal] = useState(false);
    const [isDocumentsModal, setisDocumentsModal] = useState(false);
    const [orderDocuments, setOrderDocuments] = React.useState<OrderDocumentsModel[] | undefined>(undefined);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;

    React.useEffect(() => {
        if (!orderDocuments && order && order.erpOrderNumber){
            const orderDocumentsRequest: OrderDocumentsRequestModel = {
                orderNumber: order.erpOrderNumber,
            }
            getOrderDocuments({
                orderDocumentsRequest,
                onSuccess: (result: OrderDocumentsResponseModel) => {
                    setOrderDocuments(result.orderDocuments);
                }
            })
        }
    }, [order]);
    
    if (!order) {
        return null;
    }
    const onClickCancel = () => {
        cancelOrder({
            onSuccess: () => {
                setIsCancelOrderModal(false);
                toasterContext.addToast({
                    body: siteMessage(translate("Order Successfully Canceled.")),
                    messageType: "success",
                });
                setIsCancelOrderModal(false);
            },
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: errorMessage, messageType: "danger" });
                setIsCancelOrderModal(false);
            },
        });
    };

    const CancelOrderModalOpen = () => {
        setIsCancelOrderModal(true);
    };

    const closeCancelOrder = () => {
        setIsCancelOrderModal(false);
    };

    const DocumentsModalOpen = () => {
        setisDocumentsModal(true);
    };

    const DocumentsModalClose = () => {
        setisDocumentsModal(false);
    };

    const onClickPrintInvoice = () => {
        const invoiceUrl = "/api/v1/invoice";
        const customerNumber = order.customerNumber;
        const orderNumber = order.erpOrderNumber;
        window.open(`${invoiceUrl}/${customerNumber}/${orderNumber}`, "_blank");
    };

    const onClickPrint = () => {
        if (!order) {
            return;
        }
        openPrintDialog();
    };
    const buttonList: ButtonMapper = {};
    if (order.canAddToCart && canCancel && order.properties?.canCancleOrder === "true") {
        buttonList[buttons.cancel] = {
            button: (
                <Button {...styles.cancelButton} onClick={CancelOrderModalOpen}>
                    <Icon className="icon-close" src="XCircle" />
                    <span>{translate("Request cancellation")}</span>
                </Button>
            ),
            clickable: (
                <Clickable {...styles.cancelButton} onClick={CancelOrderModalOpen}>
                    <Icon className="icon-close" src="XCircle" />
                    <span>{translate("Request cancellation")}</span>
                </Clickable>
            ),
        };
    }

    if (allowRma && rmaLink) {
        buttonList[buttons.rma] = {
            button: (
                <Clickable
                    {...styles.rmaClickable}
                    data-test-selector="orderDetails_returnRequestButton"
                    target="_blank"
                    href="https://testequity.zendesk.com/hc/en-us/requests/new?ticket_form_id=16421333123469"
                >
                    <Icon src={ReturnIcon} />
                    <span>{translate("Request Return")}</span>
                </Clickable>
            ),
            clickable: (
                <Clickable
                    {...styles.rmaClickable}
                    target="_blank"
                    href="https://testequity.zendesk.com/hc/en-us/requests/new?ticket_form_id=16421333123469"
                >
                    <Icon src={ReturnIcon} />
                    <span>{translate("Request Return")}</span>
                </Clickable>
            ),
        };
    }

    buttonList[buttons.email] = {
        button: (
            <ShareEntityButton
                entityId={order.webOrderNumber}
                entityName="Order"
                extendedStyles={styles.shareEntityButtonStyles}
                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
            />
        ),
        clickable: (
            <ShareEntityButton
                entityId={order.webOrderNumber}
                entityName="Order"
                variant="clickable"
                extendedStyles={styles.shareEntityButtonStyles}
                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
            />
        ),
    };

    buttonList[buttons.print] = {
        button: (
            <Button {...styles.printButton} onClick={onClickPrint}>
                <Icon src="Printer" />
                <span>{translate("Print Order")}</span>
            </Button>
        ),
        clickable: (
            <Clickable {...styles.printClickable} onClick={onClickPrint}>
                <Icon src="Printer" />
                <span>{translate("Print Order")}</span>
            </Clickable>
        ),
    };

    if (order.status === "4" || order.status === "5") {
        buttonList[buttons.printinvoice] = {
            button: (
                <Button {...styles.printinvoiceButton} onClick={onClickPrintInvoice} className="printInvoiceBtn">
                    <Icon src={PrintInvoice} />
                    <span>{translate("Print Invoice")}</span>
                </Button>
            ),
            clickable: (
                <Clickable {...styles.printinvoiceClickable} onClick={onClickPrintInvoice} className="printInvoiceBtn">
                    <Icon src={PrintInvoice} />
                    <span>{translate("Print Invoice")}</span>
                </Clickable>
            ),
        };
    }

    if (order.erpOrderNumber && orderDocuments !== undefined && orderDocuments.length > 0) {
        buttonList[buttons.documents] = {
            button: (
                <Button {...styles.documentButton} onClick={DocumentsModalOpen}>
                    <Icon src={Document} />
                    <span>{translate("Documents")}</span>
                </Button>
            ),
            clickable: (
                <Clickable {...styles.documentButton} onClick={DocumentsModalOpen}>
                    <Icon src={Document} />
                    <span>{translate("Documents")}</span>
                </Clickable>
            ),
        };
    }

    const buttonsToRender = fields.buttonsOrder.map(button => buttonList[button.fields.name]).filter(button => button);

    if (typeof document !== "undefined") {
        setTimeout(() => {
            const btnWrappers = document.querySelectorAll(".btn-wrapper");

            btnWrappers.forEach(function (btnWrapper) {
                const firstChild = btnWrapper.children[0];
                if (firstChild && firstChild.tagName !== "A" && firstChild.tagName !== "BUTTON") {
                    // remove empty element
                    btnWrapper.remove();
                    btnWrapper.classList.add("hide-empty-element");
                }
            });
        }, 1000);
    }

    return (
        <>
            <StyledWrapper {...styles.buttonsWrapper}>
                {buttonsToRender.map((button, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper className="btn-wrapper" {...styles.buttonWrapper} key={index}>
                        {button.button}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
            <CancelOrderModal
                isOpen={isCancelOrderModal}
                handleClose={closeCancelOrder}
                onClickCancel={onClickCancel}
                translate={translate}
                cancelOrderModalStyles={styles}
            />
            <DocumentModal
                isOpen={isDocumentsModal}
                handleClose={DocumentsModalClose}
                translate={translate}
                orderDocuments={orderDocuments}
                orderNumber={order.webOrderNumber}
                documentModalStyles={styles}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderDetailsButtonSet)),
    definition: {
        allowedContexts: [OrderDetailsPageContext, OrderStatusPageContext],
        displayName: "Order Details Button Set",
        group: "Order Details",
        fieldDefinitions: [
            {
                name: fields.buttonsOrder,
                displayName: "Buttons Order",
                editorTemplate: "ListField",
                tooltipPosition: "left",
                tooltip:
                    "RMA, Print, Email, Cancel, Printinvoice, Documents",
                defaultValue: [
                    { fields: { name: buttons.rma } },
                    { fields: { name: buttons.print } },
                    { fields: { name: buttons.email } },
                    { fields: { name: buttons.cancel } },
                    // { fields: { name: buttons.reorder } },
                    { fields: { name: buttons.printinvoice }},
                    { fields: { name: buttons.documents } }
                ],

                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                hideAdd: false,
                hideEdit: false,
                hideDelete: false,
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the order generates an email attachment using the Order Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
